import React, { useEffect, useState } from 'react';

import { nanoid } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';

import { bemCssModules, getTestId } from '@utils';
import { useLocation, useNavigate } from '@utils/hooks';
import { LocationStateType } from '@utils/types';

import { accountsActions, notificationsActions, useStore } from '@modules';
import { AccountsWithdrawStatusesEnum } from '@modules/accounts/types';
import { NotificationTypesEnum } from '@modules/notifications/types';
import { VerifyNextEnum } from '@modules/types';

import Verification from '@containers/Verification';
import EmailTooltip from '@containers/Verification/EmailTooltip';
import PhoneTooltip from '@containers/Verification/PhoneTooltip';
import LoginLayout from '@layouts/LoginLayout';

import { useCustomContext } from '../context';

import style from './SendVerification.scss';

const cn = bemCssModules(style, 'SendVerification');
const ti = getTestId('SendVerification');

const SendVerification: CustomFC = ({ className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [resendButtonId] = useState(nanoid());
  const [{ accounts, user }, dispatch] = useStore();
  const { iban_currency: currency } = accounts;
  const error = accounts.errors?.data?.code?.[0] || '';
  const isLoading = !!accounts.loaders.accountWithdrawVerify;
  const { succeeded } = AccountsWithdrawStatusesEnum;

  const location = useLocation<LocationStateType['accountsSendVerification']>();
  const { state } = location;
  const { next } = state;

  const type = next === VerifyNextEnum.verifyPhone ? 'phone' : 'email';

  const { context } = useCustomContext();
  const { amount } = context;

  useEffect(() => {
    return () => {
      dispatch(accountsActions.clearVerifyData());
    };
  }, []);

  const handleSubmit = (code: string, key: string) => {
    dispatch(accountsActions.accountWithdrawVerify({ key, code, type }))
      .unwrap()
      .then(res => {
        const { status } = res.data;
        if (status === succeeded) {
          const data = {
            title: t('notifications.thanks'),
            message: t('notifications.sucessfully_sent', { amount, currency }),
            canHide: false,
          };
          dispatch(notificationsActions.add(data));
        } else {
          const data = {
            title: t('notifications.payment_declined.title'),
            message: t('notifications.payment_declined.message'),
            type: NotificationTypesEnum.error,
            canHide: false,
          };
          dispatch(notificationsActions.add(data));
        }
        navigate(CONST.nav.accounts);
      });
  };

  const tooltip =
    type === 'phone' ? <PhoneTooltip id={resendButtonId} /> : <EmailTooltip id={resendButtonId} />;

  return (
    <LoginLayout>
      <div className={cn(null, [className])} data-testid={ti()}>
        <Verification
          description={t('withdraw.verify_description', { field: user.data?.[type] })}
          customInputClass={cn('custom-input')}
          onVerify={handleSubmit}
          isLoading={isLoading}
          type={type}
          error={error}
          tooltip={tooltip}
          id={resendButtonId}
          isAccountsWithdraw
        />
      </div>
    </LoginLayout>
  );
};

export default SendVerification;
